import { useEffect, useId, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useFetcher, useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';

import StatusInputs from '~/components/Forms/StatusInputs';
import { usePageContext } from '~/context/usePageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useEmail from '~/hooks/useEmail';
import { getOrCreateAnonymousUserId } from '~/hooks/useNectarAnalytics';
import useToast from '~/hooks/useToast';
import { LocalStorageKeys } from '~/interfaces/local_storage';
import type { loader } from '~/root';
import DataLayer from '~/services/DataLayer';
import { is_iOS } from '~/utils/deviceTest';

import { SubscriberGate } from '../types';
import { SignupProvider } from './SignupProvider';

interface ClientSideFormProps {
  visitToken?: string;
  referralId?: string;
  boostAffiliateRecommendingPublicationId?: string | null;
}

const ClientSideFormInputs = ({
  visitToken,
  referralId,
  boostAffiliateRecommendingPublicationId,
}: ClientSideFormProps) => {
  return (
    <ClientOnly fallback={null}>
      {() => (
        <>
          <input hidden name="ref" value={referralId || ''} onChange={() => null} />
          <input hidden name="bhba" value={boostAffiliateRecommendingPublicationId || ''} onChange={() => null} />
          <input hidden name="visit_token" value={visitToken || ''} onChange={() => null} />
        </>
      )}
    </ClientOnly>
  );
};

interface SignupActionProps {
  type?: SubscriberGate;
  onEmailChange?: (email: string) => void;
  onSuccess?: () => void;
  autoFocus?: boolean;
  premiumOfferId?: string;
  children: React.ReactNode;
  bodyTextColor?: string;
}

const SignupAction = ({
  type = SubscriberGate.NONE,
  onEmailChange,
  onSuccess,
  autoFocus = false,
  premiumOfferId,
  children,
  bodyTextColor,
}: SignupActionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { publication } = useRouteLoaderData<typeof loader>('root');

  const { setCookie, cookies, visitToken, referralId, currentPath, postId } = usePageContext();

  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);

  const { pathname } = useLocation();
  const [email, setEmail] = useEmail();
  const formRef = useRef<HTMLFormElement>(null);

  // Compliance
  const [acceptToSChecked, setAcceptToSChecked] = useState<boolean>(false);
  const consentTextId = useId();

  // Nectar params: set on client so they are in the form when submitted
  const [nectarParams, setNectarParams] = useState({
    signupUrl: '',
    anonymousUserId: '',
    sendableId: '',
    sendableType: '',
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setNectarParams({
      signupUrl: window.location.href,
      anonymousUserId: getOrCreateAnonymousUserId(),
      sendableId: postId ?? '',
      sendableType: postId ? 'post' : '',
    });
  }, [postId]);

  // Current fetcher state
  const fetcher = useFetcher();
  const fetcherData: any = fetcher.data;
  const isSubmitting = fetcher.state === 'submitting';

  // Set up redirect details
  const signupRedirects = publication.web_template.signup_flow;
  const enabledRedirects = signupRedirects.filter(({ enabled }: any) => enabled);
  const firstRedirectInFlow = enabledRedirects[0];
  const triggerRedirect = enabledRedirects.length > 0 && enabledRedirects?.[0]?.managed_type !== 'custom';
  const isFirstRedirectExternal =
    Boolean(firstRedirectInFlow) && !firstRedirectInFlow.full_url.startsWith(publication.url);

  const isPopup = type === SubscriberGate.POPUP;
  const isGated = type === SubscriberGate.GATED;
  const isForm = type === SubscriberGate.FORM;
  const isNoRedirect = isPopup || isGated || isForm;

  const redirectPath = useMemo(() => {
    if (isNoRedirect) return pathname; // no redirect
    if (firstRedirectInFlow) {
      const splitUrl = firstRedirectInFlow.href.split('?');
      const url = splitUrl[0];
      const queryParams = splitUrl[1] || '';
      const searchParams = new URLSearchParams(queryParams);
      searchParams.set('email', email);
      return `${url}?${searchParams.toString()}`;
    }

    return '/';
  }, [firstRedirectInFlow, pathname, email, isNoRedirect]);

  // Get other settings from publication
  const {
    double_opt_required: isDoubleOptRequired,
    has_referral_program: isReferralProgram,
    private: isPrivate,
    require_subscriber_approval: requireSubscriberAppoval,
  } = publication;

  const shouldDoubleOpt = Boolean(isDoubleOptRequired || (isReferralProgram && referralId));

  useToast(fetcherData?.toast);
  useEffect(() => {
    if (fetcherData?.toast?.status === 'success') {
      setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });
      formRef?.current?.reset(); // Reset form on successful submission
    }

    // Send datalayer event before any redirects
    if (fetcherData?.dataLayerEvent) {
      DataLayer.pushEvent(fetcherData.dataLayerEvent);
    }

    // Adds the redirects into local storage to let the client know it is in a post-signup flow
    if (!isNoRedirect && fetcherData?.redirectTo && triggerRedirect) {
      const customRedirectState = {
        startedAt: new Date().toISOString(), // We use this to determine if the flow has expired so users don't wind up in the flow if they refresh the page
        currentIndex: 0,
        nextIndex: 1,
        redirects: enabledRedirects,
      };

      localStorage.setItem(LocalStorageKeys.SIGNUP_REDIRECTS, JSON.stringify(customRedirectState));
    }

    // Handle redirects if any
    if (!isNoRedirect && fetcherData?.redirectTo) {
      // Handle external vs. internal redirects
      if (Boolean(firstRedirectInFlow) && isFirstRedirectExternal) {
        setTimeout(() => {
          // ios devices don't like window.open, so we redirect them directly
          if (is_iOS(navigator.userAgent)) {
            try {
              window.location = fetcherData?.redirectTo;
            } catch {
              window.location = fetcherData?.redirectTo;
            }
          } else {
            window.open(fetcherData?.redirectTo, '_blank');
            navigate('/');
          }
        }, 1000);
      } else {
        navigate(fetcherData?.redirectTo);
      }
    }

    // Optional callback on successful submission, possibly useful in places where we don't redirect and still want to have a follow up action.
    if (fetcherData?.redirectTo && onSuccess) {
      onSuccess();
    }
  }, [fetcherData]);

  //  Success Message Based on Publication Settings
  const getToastMessage = () => {
    if (publication.require_subscriber_approval) {
      return t('notifications.subscribe.require_approval');
    }

    if (publication.double_opt_required) {
      return t('notifications.subscribe.confirm');
    }

    return t('notifications.subscribe.subscribed');
  };

  // With the introduction of long-term caching from Cloudflare CDNs for the site we ran into a new problem
  // On some very rare occasions (only impacting a small amount of sites), their sites were being cached without visit tokens provided in the create action.
  // This was causing the subscription events to fail on repeat in the cached state.
  // Signing up for publications is a p0 and so this newly introduced logic does the following to prevent the described issue above:
  // Intercepts the form submission on the client. Double checks to see if the client side values are present and adds them if they are.
  // If we ever turn off caching, this solution likely is no longer necessary and we can remove this method entirely.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>, premiumOfferId?: string | null) => {
    e.preventDefault();

    if (publication?.enable_signup_confirmation && !acceptToSChecked) {
      toast.error('Accept ToU & Privacy Policy.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const hasVisitToken = formData.get('visit_token');
    const hasRef = formData.get('ref');
    const trimmedEmail = email?.trim();
    formData.set('is_js_enabled', 'true');
    formData.set('accepted_tos', String(acceptToSChecked));
    formData.set('email', trimmedEmail);

    // We get a lot of headless bots that submit empty emails. This will remove those from actual submissions and processed sentry errors.
    if (!trimmedEmail) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!hasVisitToken && visitToken) {
      formData.set('visit_token', visitToken);
    }

    if (!hasRef && referralId) {
      formData.set('ref', referralId);
    }

    if (premiumOfferId) {
      formData.set('premium_offer_id', premiumOfferId);
    }

    if (typeof window !== 'undefined') {
      formData.set('nectar_signup_url', window.location.href);
      formData.set('nectar_anonymous_user_id', getOrCreateAnonymousUserId());
      if (postId) {
        formData.set('nectar_sendable_id', postId);
        formData.set('nectar_sendable_type', 'post');
      }
    }

    fetcher.submit(formData, {
      method: 'post',
      action: '/create',
    });
  };

  if (isPrivate && !requireSubscriberAppoval) return null;

  return (
    <div className="flex flex-col items-center w-full">
      <fetcher.Form
        ref={formRef}
        method="post"
        action="/create"
        className="w-full bg-transparent group rounded-wt"
        onSubmit={(e) => handleFormSubmit(e, premiumOfferId)}
      >
        <ClientSideFormInputs
          visitToken={visitToken}
          referralId={referralId}
          boostAffiliateRecommendingPublicationId={boostAffiliateRecommendingPublicationId}
        />
        <input name="redirect_path" type="hidden" value={redirectPath} />
        <input name="sent_from_orchid" type="hidden" value="true" />
        <input name="fallback_path" type="hidden" value={currentPath} />
        <input name="nectar_signup_url" type="hidden" value={nectarParams.signupUrl} />
        <input name="nectar_anonymous_user_id" type="hidden" value={nectarParams.anonymousUserId} />
        {nectarParams.sendableId ? (
          <>
            <input name="nectar_sendable_id" type="hidden" value={nectarParams.sendableId} />
            <input name="nectar_sendable_type" type="hidden" value={nectarParams.sendableType} />
          </>
        ) : null}
        <input name="double_opt" type="hidden" value={shouldDoubleOpt.toString()} />
        <input name="trigger_redirect" type="hidden" value={triggerRedirect.toString()} />
        <StatusInputs name="subscribe" successMessage={getToastMessage()} errorMessage={t('errors.default')} />
        <div className="flex flex-col">
          <SignupProvider
            email={email}
            setEmail={setEmail}
            onEmailChange={onEmailChange}
            isSubmitting={isSubmitting}
            autoFocus={autoFocus}
          >
            {children}
          </SignupProvider>
          {publication?.enable_signup_confirmation && (
            <div className="flex flex-row mt-3 space-x-2">
              <input
                type="checkbox"
                className="w-4 h-4 border-gray-300 rounded text-lp-main-signup-cta-color focus:ring-indigo-500"
                checked={acceptToSChecked}
                onChange={() => setAcceptToSChecked(!acceptToSChecked)}
                aria-labelledby={consentTextId}
              />
              {!publication?.terms_of_service || publication?.terms_of_service.length === 0 ? (
                <p
                  id={consentTextId}
                  className="text-xs font-medium"
                  style={{
                    color: bodyTextColor,
                  }}
                >
                  {t('shared.cta.gdpr_signup_consent')}{' '}
                  <a
                    href="https://www.beehiiv.com/tou"
                    target="_blank"
                    className="underline underline-offset-1 hover:opacity-80"
                    rel="noreferrer"
                  >
                    {t('footer.terms_of_use')}
                  </a>{' '}
                  {t('shared.cta.and')}{' '}
                  <a
                    href="https://www.beehiiv.com/privacy"
                    target="_blank"
                    className="underline underline-offset-1 hover:opacity-80"
                    rel="noreferrer"
                  >
                    {t('footer.privacy_policy')}
                  </a>
                  .
                </p>
              ) : (
                <p
                  id={consentTextId}
                  className="text-xs font-medium"
                  style={{
                    color: bodyTextColor,
                  }}
                >
                  {t('shared.cta.gdpr_signup_consent')} {t('shared.cta.sign_up')}{' '}
                  <a
                    href="/tos"
                    target="_blank"
                    className="underline underline-offset-1 hover:opacity-80"
                    rel="noreferrer"
                  >
                    {t('meta.titles.tos')}
                  </a>
                  .
                </p>
              )}
            </div>
          )}
        </div>
      </fetcher.Form>
    </div>
  );
};

export default SignupAction;
